import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Package, X, Mail, FileText, Shield, Download, Plus, Eye
} from 'lucide-react';
import { Button } from '../../ui/button';
import { API, formatPrice, formatDate, formatActivityType, getActivityTypeBadgeClass } from '../utils';

const PackName = ({ name }) => <span>{name}</span>;

const RentalsView = ({ reservations }) => {
const activeRentals = reservations.filter(r => r.subscription_status === 'active' || r.status === 'confirmed');
    const [selectedRental, setSelectedRental] = useState(null);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [emailType, setEmailType] = useState('checkup');
    const [emailMessage, setEmailMessage] = useState('');
    const [emailAttachment, setEmailAttachment] = useState(null);
    const [sending, setSending] = useState(false);
    const emailFormRef = React.useRef(null);

    // Auto-scroll to email form when it opens
    React.useEffect(() => {
      if (showEmailForm && emailFormRef.current) {
        emailFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, [showEmailForm]);

    const emailTemplates = {
      checkup: "Bonjour,\n\nNous espérons que votre location se passe bien et que vous êtes satisfait de nos écrans LED.\n\nN'hésitez pas à nous contacter si vous avez besoin d'assistance technique ou si vous avez des questions.\n\nCordialement,",
      return_request: "Bonjour,\n\nNous vous contactons concernant le retour de votre matériel de location.\n\nVeuillez trouver ci-joint votre bordereau de retour.\n\nMerci de nous confirmer vos disponibilités pour la restitution du matériel.\n\nCordialement,",
      payment_reminder: "Bonjour,\n\nNous vous rappelons qu'un paiement est en attente pour votre location.\n\nMerci de régulariser votre situation dans les meilleurs délais.\n\nCordialement,",
      custom: ""
    };

    const handleSelectRental = (rental) => {
      setSelectedRental(rental);
      setShowEmailForm(false);
      setEmailAttachment(null);
    };

    const handleEmailTypeChange = (type) => {
      setEmailType(type);
      setEmailMessage(emailTemplates[type] || '');
    };

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        // Max 10MB
        if (file.size > 10 * 1024 * 1024) {
          toast.error('Fichier trop volumineux (max 10 Mo)');
          return;
        }
        setEmailAttachment(file);
      }
    };

    const handleSendEmail = async () => {
      if (!emailMessage.trim()) {
        toast.error('Veuillez entrer un message');
        return;
      }
      setSending(true);
      try {
        const formData = new FormData();
        formData.append('type', emailType);
        formData.append('message', emailMessage);
        if (emailAttachment) {
          formData.append('attachment', emailAttachment);
        }
        
        await axios.post(`${API}/admin/reservations/${selectedRental.id}/send-email`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Email envoyé avec succès');
        setShowEmailForm(false);
        setEmailMessage('');
        setEmailAttachment(null);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
      } finally {
        setSending(false);
      }
    };

    const downloadContract = (rental) => {
      if (rental.id) {
        // Use the API endpoint to generate and download the contract PDF
        window.open(`${API}/contracts/${rental.id}/pdf`, '_blank');
      } else {
        toast.error('Contrat PDF non disponible');
      }
    };

    return (
      <div className="flex gap-4 h-[calc(100vh-200px)]">
        {/* List */}
        <div className={`${selectedRental ? 'w-1/2' : 'w-full'} space-y-4 overflow-y-auto transition-all`}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Parc en location</h2>
            <p className="text-sm text-slate-400">{activeRentals.length} location(s) active(s)</p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {activeRentals.map((res) => (
              <div 
                key={res.id} 
                className={`bg-slate-900 rounded-xl border p-5 cursor-pointer transition-all hover:border-[#DFFF00]/50 ${
                  selectedRental?.id === res.id ? 'border-[#DFFF00]' : 'border-slate-800'
                }`}
                onClick={() => handleSelectRental(res)}
                data-testid={`rental-card-${res.id?.slice(0, 8)}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-white"><PackName name={res.pack_name} /></p>
                    <p className="text-sm text-slate-400">{res.pack_surface}</p>
                  </div>
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">Actif</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#DFFF00] flex items-center justify-center">
                      <span className="text-black font-medium text-xs">{res.customer?.firstName?.[0]}{res.customer?.lastName?.[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm text-white">{res.customer?.firstName} {res.customer?.lastName}</p>
                      <p className="text-xs text-slate-500">{res.customer?.company || 'Particulier'}</p>
                    </div>
                  </div>
                  <p className="text-[#DFFF00] font-medium">{formatPrice(res.monthly_price)}/mois</p>
                </div>
              </div>
            ))}
          </div>

          {activeRentals.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Aucune location active</p>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedRental && (
          <div className="w-1/2 bg-slate-900 rounded-xl border border-slate-800 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 p-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white"><PackName name={selectedRental.pack_name} /></h3>
                <p className="text-sm text-slate-400">{selectedRental.pack_surface}</p>
              </div>
              <Button variant="ghost" size="sm" className="text-slate-400" onClick={() => setSelectedRental(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-5 space-y-6">
              {/* Client Info */}
              <div>
                <h4 className="text-sm font-semibold text-slate-400 uppercase mb-3">Client</h4>
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#DFFF00] flex items-center justify-center">
                      <span className="text-black font-bold">{selectedRental.customer?.firstName?.[0]}{selectedRental.customer?.lastName?.[0]}</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{selectedRental.customer?.firstName} {selectedRental.customer?.lastName}</p>
                      <p className="text-sm text-slate-400">{selectedRental.customer?.company || 'Particulier'}</p>
                      {selectedRental.customer?.activityType && (
                        <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full border ${getActivityTypeBadgeClass(selectedRental.customer.activityType)}`}>
                          {formatActivityType(selectedRental.customer.activityType)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm pt-2">
                    <div>
                      <span className="text-slate-500">Email:</span>
                      <p className="text-slate-300">{selectedRental.customer?.email}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Téléphone:</span>
                      <p className="text-slate-300">{selectedRental.customer?.phone || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rental Info */}
              <div>
                <h4 className="text-sm font-semibold text-slate-400 uppercase mb-3">Location</h4>
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Loyer mensuel</span>
                    <span className="text-[#DFFF00] font-bold">{formatPrice(selectedRental.monthly_price)}/mois</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Caution</span>
                    <span className="text-white font-medium">{formatPrice(selectedRental.caution || selectedRental.monthly_price)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Statut caution</span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">Encaissée</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Début location</span>
                    <span className="text-white">{formatDate(selectedRental.confirmed_at || selectedRental.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h4 className="text-sm font-semibold text-slate-400 uppercase mb-3">Documents</h4>
                <div className="space-y-2">
                  <button 
                    className="w-full flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors"
                    onClick={() => downloadContract(selectedRental)}
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-[#DFFF00]" />
                      <span className="text-white">Contrat signé</span>
                    </div>
                    <Download className="w-4 h-4 text-slate-400" />
                  </button>
                  {selectedRental.rc_document_name && (
                    <button 
                      className="w-full flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors"
                      onClick={() => window.open(`${API.replace('/api', '')}/uploads/${selectedRental.rc_document_name}`, '_blank')}
                    >
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-green-400" />
                        <span className="text-white">Attestation RC</span>
                      </div>
                      <Download className="w-4 h-4 text-slate-400" />
                    </button>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div>
                <h4 className="text-sm font-semibold text-slate-400 uppercase mb-3">Actions</h4>
                <div className="space-y-2">
                  <Button 
                    className="w-full bg-[#DFFF00] hover:bg-[#c8e600] text-black justify-start"
                    onClick={() => { setShowEmailForm(true); handleEmailTypeChange('checkup'); }}
                    data-testid="send-email-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Envoyer un email
                  </Button>
                </div>
              </div>

              {/* Email Form */}
              {showEmailForm && (
                <div ref={emailFormRef} className="border-t border-slate-800 pt-4">
                  <h4 className="text-sm font-semibold text-slate-400 uppercase mb-3">Envoyer un email</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-slate-400 block mb-2">Type de message</label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { id: 'checkup', label: '👋 Prise de nouvelles' },
                          { id: 'return_request', label: '📦 Demande retour' },
                          { id: 'payment_reminder', label: '💳 Relance paiement' },
                          { id: 'custom', label: '✏️ Personnalisé' }
                        ].map(type => (
                          <button
                            key={type.id}
                            onClick={() => handleEmailTypeChange(type.id)}
                            className={`p-2 rounded-lg text-xs font-medium transition-colors ${
                              emailType === type.id 
                                ? 'bg-[#DFFF00] text-black' 
                                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                            }`}
                          >
                            {type.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-slate-400 block mb-2">Message</label>
                      <textarea 
                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 h-32 text-sm"
                        placeholder="Votre message..."
                        value={emailMessage}
                        onChange={(e) => setEmailMessage(e.target.value)}
                      />
                    </div>
                    {/* Pièce jointe */}
                    <div>
                      <label className="text-sm text-slate-400 block mb-2">Pièce jointe (optionnel)</label>
                      <div className="flex items-center gap-3">
                        <label className="flex-1 cursor-pointer">
                          <div className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed transition-colors ${
                            emailAttachment 
                              ? 'border-[#DFFF00] bg-[#DFFF00]/10' 
                              : 'border-slate-700 hover:border-slate-500 bg-slate-800/50'
                          }`}>
                            {emailAttachment ? (
                              <>
                                <FileText className="w-5 h-5 text-[#DFFF00]" />
                                <span className="text-sm text-[#DFFF00] font-medium truncate max-w-[200px]">
                                  {emailAttachment.name}
                                </span>
                              </>
                            ) : (
                              <>
                                <Plus className="w-5 h-5 text-slate-400" />
                                <span className="text-sm text-slate-400">Ajouter un fichier</span>
                              </>
                            )}
                          </div>
                          <input 
                            type="file" 
                            className="hidden" 
                            onChange={handleFileChange}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          />
                        </label>
                        {emailAttachment && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-400 hover:text-red-300"
                            onClick={() => setEmailAttachment(null)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">PDF, DOC, JPG, PNG (max 10 Mo)</p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-[#DFFF00] hover:bg-[#c8e600] text-black"
                        onClick={handleSendEmail}
                        disabled={sending}
                      >
                        {sending ? 'Envoi...' : 'Envoyer'}
                      </Button>
                      <Button 
                        variant="outline" 
                        className="border-slate-700 text-slate-300"
                        onClick={() => setShowEmailForm(false)}
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  

}

export default RentalsView;
